/* ============================================================
 * GoodBuyz Main Navigation — progressive enhancement
 *
 * The nav renders and functions without this file. Markup is
 * server-rendered Liquid and the dropdowns are <details> elements, so
 * open/close, keyboard access and no-JS all work already.
 *
 * This adds only what CSS cannot:
 *   - the 400ms pause between click and open (DEV NOTE D1)
 *   - closing an open dropdown when another one opens
 *   - click-outside and Escape to close dropdowns
 *
 * Note the deliberate asymmetry with the mega menu: DEV NOTE D6 says the
 * mega menu may ONLY be closed by its two X buttons, so it is excluded
 * from the click-outside handler here. gb-shop-menu.js owns it.
 * ============================================================ */

(function () {
  'use strict';

  function init() {
    var nav = document.querySelector('[data-gb-main-nav]');
    if (!nav) return;

    var delay = 400;
    if (nav.dataset.gbOpenDelay) {
      var parsed = parseInt(nav.dataset.gbOpenDelay, 10);
      if (!isNaN(parsed)) delay = parsed;
    }

    var details = Array.prototype.slice.call(nav.querySelectorAll('[data-gb-dropdown]'));
    /* No early return on an empty `details` list: a bar with only a mega
       block and plain links still needs its open-state tracked, or the
       division hairline never appears. */

    var openTimer = null;

    var bar = nav.closest('.gb-main-nav-bar') || nav;

    function anyOpen() {
      if (details.some(function (d) { return d.open; })) return true;
      var t = nav.querySelector('.gb-shop-trigger[aria-expanded="true"]');
      return !!t;
    }

    // The bar's lower hairline becomes the division between the bar and the
    // open panel, so it needs to know when anything is open.
    function syncBarState() {
      bar.classList.toggle('is-menu-open', anyOpen());
    }

    function closeAll(except) {
      details.forEach(function (d) {
        if (d !== except) d.open = false;
      });
      syncBarState();
    }

    /* Hover-to-switch across the top bar has been REMOVED.
     *
     * It was added on 2026-08-25 by request, on top of dev note D3: once a menu
     * was open, moving across the bar switched to the hovered item. The design
     * has since moved to click everywhere, so D3 stands on its own again and
     * every dropdown now opens only from its summary, with the same 400ms pause.
     *
     * `data-gb-no-hover` on the grid panel existed solely to opt that panel out
     * of this behaviour. It is now inert but left in the markup: it costs
     * nothing, and removing it would touch a snippet for no functional gain. */

    // Keep the bar state in step with the mega menu, which gb-shop-menu.js owns.
    var megaTrigger = nav.querySelector('.gb-shop-trigger');
    if (megaTrigger && window.MutationObserver) {
      new MutationObserver(syncBarState).observe(megaTrigger, {
        attributes: true,
        attributeFilter: ['aria-expanded']
      });
    }

    details.forEach(function (d) {
      d.addEventListener('toggle', syncBarState);
    });

    details.forEach(function (d) {
      var summary = d.querySelector('summary');
      if (!summary) return;

      summary.addEventListener('click', function (e) {
        // Already open: let the browser close it immediately. Closing
        // should never feel delayed.
        if (d.open) return;

        // DEV NOTE D1 — 400ms pause between click and open.
        e.preventDefault();

        if (openTimer) clearTimeout(openTimer);
        openTimer = setTimeout(function () {
          closeAll(d);
          d.open = true;
        }, delay);
      });

      // Keyboard activation should not be delayed — it is already
      // deliberate, and a delay reads as unresponsiveness.
      summary.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') {
          if (openTimer) clearTimeout(openTimer);
          closeAll(d);
        }
      });
    });

    // Click outside closes dropdowns. The mega menu is excluded on
    // purpose — see DEV NOTE D6 in the header comment.
    document.addEventListener('click', function (e) {
      if (nav.contains(e.target)) return;
      if (openTimer) clearTimeout(openTimer);
      closeAll(null);
    });

    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape') return;

      var open = details.filter(function (d) { return d.open; });
      if (!open.length) return;

      if (openTimer) clearTimeout(openTimer);
      closeAll(null);

      // Return focus so keyboard users are not stranded.
      var summary = open[0].querySelector('summary');
      if (summary) summary.focus();
    });

    /* ----------------------------------------------------------
     * Mobile drawer
     *
     * Ours, not the theme's. `.mobileMenu-toggle` and the handler at
     * assets/theme.js:575 open Ella's halo-sidebar; that button lives in
     * sections/header-mobile.liquid, which is deliberately disabled. So
     * nothing here contends with the theme, and the drawer renders from
     * the same section blocks as the desktop bar.
     * ---------------------------------------------------------- */
    /* ------------------------------------------------------------
     * Mobile tile grid — the "extra buttons" under the category list
     *
     * The tiles are the bar's non-category items re-laid-out, so their
     * labels, urls and icons live in the nav blocks. Liquid cannot read
     * them where the grid is rendered: for theme blocks section.blocks
     * returns opaque entries — type '@theme', every setting empty — so the
     * grid built itself from nothing and came out empty.
     *
     * Each block therefore renders its own tile into a <template> inside
     * its <li>, and this moves them into the grid's two columns. Liquid
     * still owns the markup; this only relocates it.
     *
     * Nothing is lost without JS: the drawer only opens by script, so a
     * browser that could not run this could never have seen the grid.
     * ---------------------------------------------------------- */
    var tiles = document.querySelector('[data-gb-tiles]');

    if (tiles) {
      var cols = {
        left: tiles.querySelector('[data-gb-tiles-col="left"]'),
        right: tiles.querySelector('[data-gb-tiles-col="right"]')
      };
      var sources = document.querySelectorAll('[data-gb-tile]');
      var leftCount = parseInt(tiles.getAttribute('data-gb-left-count'), 10);
      if (isNaN(leftCount)) leftCount = 5;

      // The counter advances for AUTO tiles only. Pinning one tile right must
      // not push an auto tile across the boundary, or setting a single tile
      // would silently rearrange the others.
      var auto = 0;

      sources.forEach(function (tpl) {
        var col = tpl.getAttribute('data-gb-tile-col') || 'auto';
        var target;

        if (col === 'left' || col === 'right') {
          target = cols[col];
        } else {
          auto += 1;
          target = auto <= leftCount ? cols.left : cols.right;
        }

        if (target && tpl.content) target.appendChild(tpl.content.cloneNode(true));
      });

      // Rendered hidden so an empty grid does not leave a 106px gap under the
      // category list. It is only worth showing once something is in it.
      if (cols.left.children.length || cols.right.children.length) {
        tiles.hidden = false;
      }
    }

    var drawer = document.querySelector('[data-gb-drawer]');

    if (drawer) {
      var openers = document.querySelectorAll('[data-gb-drawer-open]');
      var lastOpener = null;

      function drawerOpen() { return drawer.classList.contains('is-open'); }

      /* D5 — lock the page behind the drawer.
       *
       * Just a class. The offset save/restore is GONE, along with the
       * `position: fixed` it existed to compensate for.
       *
       * That approach hid the drawer completely: a fixed ancestor becomes the
       * containing block for fixed descendants, so `.gb-drawer`'s `inset: 0`
       * resolved against the body box rather than the viewport, and the inline
       * `top: -<scrollY>px` then pushed the drawer that far above the screen. It
       * only looked correct at scroll position 0.
       *
       * `overflow: hidden` creates no containing block, and preserves the scroll
       * position by itself — so there is nothing to store and nothing to
       * restore. See the CSS for why touch-action carries the iOS case. */
      function lockPage(lock) {
        document.body.classList.toggle('gb-drawer-open', lock);
      }

      function setDrawer(open) {
        drawer.classList.toggle('is-open', open);
        drawer.setAttribute('aria-hidden', open ? 'false' : 'true');
        lockPage(open);

        openers.forEach(function (btn) {
          btn.setAttribute('aria-expanded', open ? 'true' : 'false');
        });

        if (open) {
          // Focus the close button so the drawer is immediately dismissable
          // from the keyboard, rather than leaving focus behind on the page.
          var close = drawer.querySelector('.gb-drawer__close');
          if (close) close.focus();
        } else if (lastOpener) {
          lastOpener.focus();
          lastOpener = null;
        }
      }

      openers.forEach(function (btn) {
        btn.addEventListener('click', function (e) {
          e.preventDefault();
          lastOpener = this;
          // Opening is immediate. The 400ms pause in dev note D1 is about
          // the desktop mega panel; a drawer that lags a tap reads as broken.
          setDrawer(true);
        });
      });

      drawer.querySelectorAll('[data-gb-drawer-close]').forEach(function (btn) {
        btn.addEventListener('click', function (e) {
          e.preventDefault();
          setDrawer(false);
        });
      });

      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && drawerOpen()) setDrawer(false);
      });

      // Keep focus inside the panel while it is open.
      drawer.addEventListener('keydown', function (e) {
        if (e.key !== 'Tab' || !drawerOpen()) return;

        var panel = drawer.querySelector('.gb-drawer__panel');
        if (!panel) return;

        var focusable = panel.querySelectorAll(
          'a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        if (!focusable.length) return;

        var first = focusable[0];
        var last = focusable[focusable.length - 1];

        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      });

      // A resize up to desktop leaves the drawer stranded open over a layout
      // it was never meant for, and the body scroll lock with it.
      window.addEventListener('resize', function () {
        if (window.innerWidth > 991 && drawerOpen()) setDrawer(false);
      });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
